/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2020
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import StringUtils from './StringUtils';
import BaseMoserObject from './BaseMoserObject';
import V5QueryExpressionParserTokenValidator from './V5QueryExpressionParserTokenValidator';
import IdentifierUtil from './IdentifierUtil';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/common/utils/V5QueryExpressionParserTokenValidator (static) // isLetterOrDigit, isLetter
//@import com/ibm/bi/platform/moser/common/utils/IdentifierUtil (static) // isKeyword

/**
 * item scanner.
 * @author  hex
 */
export default class ItemScanner extends BaseMoserObject{

	/**
	 * collect identifiers from expression.
	 * @param {String} expr  expression.
	 * @return {Array}  identifiers.
	 */
	static collectIdentifiers(expr) {
		var rtLst = [];
		if (expr == null || expr.length === 0) {
			return rtLst;
		}
		var length = expr.length;
		var pos = 0;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (aChar == 35) {
				pos = ItemScanner._skipMacro(expr, pos + 1);
			} else if (aChar == 39) {
				pos = ItemScanner._skipSingleQuoteString(expr, pos + 1);
			} else if (aChar == 34) {
				pos = ItemScanner._skipDoubleQuoteString(expr, pos + 1);
			} else if (aChar == 63) {
				pos = ItemScanner._skipPrompt(expr, pos + 1);
			} else if (ItemScanner._isComment(expr, pos)) {
				pos = ItemScanner._skipComment(expr, pos + 2);
			} else if (ItemScanner._isMultiLineComment(expr, pos)) {
				pos = ItemScanner._skipMultiLineComment(expr, pos + 2);
			} else if (V5QueryExpressionParserTokenValidator.isLetter(aChar) || aChar == 95) {
				var bvalid = [true];
				var parts = [];
				var nextpos = ItemScanner._skipIdentifier(expr, pos + 1, bvalid, parts);
				if (bvalid[0] && !(parts.length==0) && ItemScanner._isValidId(parts)) {
					rtLst.push(ItemScanner._generateParts(parts));
				}
				pos = nextpos;
			} else if (aChar == 91) {
				var bvalid = [true];
				var parts = [];
				var nextpos = ItemScanner._skipBracketName(expr, pos + 1, bvalid, parts);
				if (bvalid[0] && !(parts.length==0)) {
					rtLst.push(ItemScanner._generateParts(parts));
				}
				pos = nextpos;
			} else {
				++pos;
			}
		}
		return rtLst;
	}

	static _isValidId(parts) {
		return parts.length != 1 || !(IdentifierUtil.isKeyword(parts[0]));
	}

	static _generateParts(parts) {
		var p = [];
		for (var i = 0; i < parts.length; ++i) {
			p[i] = parts[i];
		}
		return p;
	}

	/**
	 * generate identifier.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @param {int} end  end point.
	 * @return {String}  identifier.
	 */
	static _generatePart(expr, st, end) {
		var sb = "";
		var length = expr.length;
		for (var i = st; i < end && i < length; ++i) {
			var c = expr.codePointAt(i);
			sb+=StringUtils.fromCodePoint(c);
		}
		return sb;
	}

	/**
	 * identifier?
	 * @param {String} expr 	expression.
	 * @param {int} st  start point.
	 * @param {boolean[]} bvalid  valid?
	 * @param {Array} parts  parts.
	 * @return {int}  next position.
	 */
	static _skipIdentifier(expr, st, bvalid, parts) {
		var nextPos = ItemScanner._skipPart(expr, st);
		var nextPosSkipWhiteSpec = ItemScanner._skipWhiteSpace(expr, nextPos);
		var length = expr.length;
		if (nextPosSkipWhiteSpec >= length) {
			bvalid[0] = true;
			parts.push(ItemScanner._generatePart(expr, st - 1, nextPos));
			return nextPosSkipWhiteSpec;
		}
		var nextChar = expr.codePointAt(nextPosSkipWhiteSpec);
		if (nextChar == 40) {
			bvalid[0] = false;
			return nextPosSkipWhiteSpec;
		}
		if (nextChar != 46) {
			bvalid[0] = true;
			parts.push(ItemScanner._generatePart(expr, st - 1, nextPos));
			return nextPosSkipWhiteSpec;
		}
		var posAfterDot = ItemScanner._skipWhiteSpace(expr, nextPosSkipWhiteSpec + 1);
		if (posAfterDot >= length) {
			bvalid[0] = false;
			return posAfterDot;
		}
		var nextnextChar = expr.codePointAt(posAfterDot);
		if (!(V5QueryExpressionParserTokenValidator.isLetter(nextnextChar)) && nextnextChar != 95) {
			bvalid[0] = false;
			return posAfterDot;
		}
		parts.push(ItemScanner._generatePart(expr, st - 1, nextPos));
		return ItemScanner._skipIdentifier(expr, posAfterDot + 1, bvalid, parts);
	}

	/**
	 * V5 ?
	 * @param {String} expr 	expression.
	 * @param {int} st  start point.
	 * @param {boolean[]} bvalid  valid?
	 * @param {Array} parts  parts.
	 * @return {int}  next position.
	 */
	static _skipBracketName(expr, st, bvalid, parts) {
		var nextPos = ItemScanner._skipBracketPart(expr, st);
		var nextPosSkipWhiteSpec = ItemScanner._skipWhiteSpace(expr, nextPos);
		var length = expr.length;
		if (nextPosSkipWhiteSpec >= length) {
			bvalid[0] = true;
			parts.push(ItemScanner._generatePart(expr, st - 1, nextPos));
			return nextPosSkipWhiteSpec;
		}
		var nextChar = expr.codePointAt(nextPosSkipWhiteSpec);
		if (nextChar == 40) {
			bvalid[0] = false;
			return nextPosSkipWhiteSpec;
		}
		if (nextChar != 46) {
			bvalid[0] = true;
			parts.push(ItemScanner._generatePart(expr, st - 1, nextPos));
			return nextPosSkipWhiteSpec;
		}
		var posAfterDot = ItemScanner._skipWhiteSpace(expr, nextPosSkipWhiteSpec + 1);
		if (posAfterDot >= length) {
			bvalid[0] = false;
			return posAfterDot;
		}
		var nextnextChar = expr.codePointAt(posAfterDot);
		if (nextnextChar != 91) {
			bvalid[0] = false;
			return posAfterDot;
		}
		parts.push(ItemScanner._generatePart(expr, st - 1, nextPos));
		return ItemScanner._skipBracketName(expr, posAfterDot + 1, bvalid, parts);
	}

	static _skipWhiteSpace(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (ItemScanner._isWhiteSpace(aChar)) {
				++pos;
			} else {
				break;
			}
		}
		return pos;
	}

	/**
	 * skip part.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  next position.
	 */
	static _skipPart(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var c = expr.codePointAt(pos);
			if (V5QueryExpressionParserTokenValidator.isLetterOrDigit(c) || c == 95) {
				++pos;
			} else {
				break;
			}
		}
		return pos;
	}

	/**
	 * skip bracket part.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  next position.
	 */
	static _skipBracketPart(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (aChar == 93) {
				if (pos == length - 1) {
					return length;
				}
				var nextPos = pos + 1;
				if (nextPos < length && expr.codePointAt(nextPos) == 93) {
					pos = nextPos + 1;
					continue;
				}
				return nextPos;
			}
			++pos;
		}
		return pos;
	}

	/**
	 * white space?
	 * @param {int} c  char.
	 * @return {boolean}  t/f.
	 */
	static _isWhiteSpace(c) {
		return c == 32 || c == 9 || c == 10 || c == 13;
	}

	/**
	 * skip macro block.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  position after macro.
	 */
	static _skipMacro(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (aChar == 35) {
				return ++pos;
			}
			if (aChar == 39) {
				pos = ItemScanner._skipSingleQuoteString(expr, pos + 1);
			} else if (aChar == 34) {
				pos = ItemScanner._skipDoubleQuoteString(expr, pos + 1);
			} else if (aChar == 63) {
				pos = ItemScanner._skipPrompt(expr, pos + 1);
			} else if (ItemScanner._isComment(expr, pos)) {
				pos = ItemScanner._skipComment(expr, pos + 2);
			} else if (ItemScanner._isMultiLineComment(expr, pos)) {
				pos = ItemScanner._skipMultiLineComment(expr, pos + 2);
			} else {
				++pos;
			}
		}
		return pos;
	}

	/**
	 * is comment?
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {boolean}  t/f.
	 */
	static _isComment(expr, st) {
		var length = expr.length;
		if (st + 1 < length) {
			var firstChar = expr.codePointAt(st);
			var secondChar = expr.codePointAt(st + 1);
			return firstChar == 47 && secondChar == 47 || firstChar == 45 && secondChar == 45;
		}
		return false;
	}

	/**
	 * is multi line comment?
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {boolean}  t/f.
	 */
	static _isMultiLineComment(expr, st) {
		var length = expr.length;
		if (st + 1 < length) {
			var firstChar = expr.codePointAt(st);
			var secondChar = expr.codePointAt(st + 1);
			return firstChar == 47 && secondChar == 42;
		}
		return false;
	}

	/**
	 * skip single quote string.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  position after single quote string.
	 */
	static _skipSingleQuoteString(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (aChar == 39) {
				if (pos == length - 1) {
					return length;
				}
				var nextPos = pos + 1;
				if (nextPos < length && expr.codePointAt(nextPos) == 39) {
					pos = nextPos + 1;
					continue;
				}
				return nextPos;
			}
			++pos;
		}
		return pos;
	}

	/**
	 * skip double quote string.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  position after double quote string.
	 */
	static _skipDoubleQuoteString(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (aChar == 34) {
				if (pos == length - 1) {
					return length;
				}
				var nextPos = pos + 1;
				if (nextPos < length && expr.codePointAt(nextPos) == 34) {
					pos = nextPos + 1;
					continue;
				}
				return nextPos;
			}
			++pos;
		}
		return pos;
	}

	/**
	 * skip prompt.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  position after prompt.
	 */
	static _skipPrompt(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (aChar == 63) {
				return ++pos;
			}
			++pos;
		}
		return pos;
	}

	/**
	 * skip comment.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  position after comment.
	 */
	static _skipComment(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (aChar == 10 || aChar == 13) {
				return ++pos;
			}
			++pos;
		}
		return pos;
	}

	/**
	 * skip multi line comment.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  position after multi line comment.
	 */
	static _skipMultiLineComment(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (aChar == 42) {
				var nextPos = pos + 1;
				if (nextPos < length && expr.codePointAt(nextPos) == 47) {
					return ++nextPos;
				}
			}
			++pos;
		}
		return pos;
	}

	static replaceIdForExpr(targetObjType, expr, targetIdforExpr, srcObjType, oldSrcId, newSrcId) {
		if (expr == null || expr.length === 0) {
			return null;
		}
		var bChanged = false;
		var sb = "";
		var srcParts = oldSrcId.split(new RegExp("\\."));
		var length = expr.length;
		var pos = 0;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			var oldPos = pos;
			if (aChar == 35) {
				pos = ItemScanner._skipMacro(expr, pos + 1);
				sb+=(ItemScanner._generatePart(expr, oldPos, pos));
			} else if (aChar == 39) {
				pos = ItemScanner._skipSingleQuoteString(expr, pos + 1);
				sb+=(ItemScanner._generatePart(expr, oldPos, pos));
			} else if (aChar == 34) {
				pos = ItemScanner._skipDoubleQuoteString(expr, pos + 1);
				sb+=(ItemScanner._generatePart(expr, oldPos, pos));
			} else if (aChar == 63) {
				pos = ItemScanner._skipPrompt(expr, pos + 1);
				sb+=(ItemScanner._generatePart(expr, oldPos, pos));
			} else if (ItemScanner._isComment(expr, pos)) {
				pos = ItemScanner._skipComment(expr, pos + 2);
				sb+=(ItemScanner._generatePart(expr, oldPos, pos));
			} else if (ItemScanner._isMultiLineComment(expr, pos)) {
				pos = ItemScanner._skipMultiLineComment(expr, pos + 2);
				sb+=(ItemScanner._generatePart(expr, oldPos, pos));
			} else if (V5QueryExpressionParserTokenValidator.isLetter(aChar) || aChar == 95) {
				var bvalid = [true];
				var parts = [];
				var nextpos = ItemScanner._skipIdentifier(expr, pos + 1, bvalid, parts);
				var b = false;
				if (bvalid[0] && !(parts.length==0) && ItemScanner._isValidId(parts)) {
					var newPart = ItemScanner._matchOp(parts, targetObjType, srcParts, srcObjType, newSrcId, false, targetIdforExpr);
					if (newPart != null) {
						sb+=(newPart);
						bChanged = true;
						b = true;
						var st = ItemScanner._getWhiteSpace(expr, nextpos - 1);
						if (st >= 0) {
							sb+=(ItemScanner._generatePart(expr, st, nextpos));
						}
					}
				}
				pos = nextpos;
				if (!b) {
					sb+=(ItemScanner._generatePart(expr, oldPos, pos));
				}
			} else if (aChar == 91) {
				var bvalid = [true];
				var parts = [];
				var nextpos = ItemScanner._skipBracketName(expr, pos + 1, bvalid, parts);
				var b = false;
				if (bvalid[0] && !(parts.length==0)) {
					var newPart = ItemScanner._matchOp(parts, targetObjType, srcParts, srcObjType, newSrcId, true, targetIdforExpr);
					if (newPart != null) {
						sb+=(newPart);
						bChanged = true;
						b = true;
						var st = ItemScanner._getWhiteSpace(expr, nextpos - 1);
						if (st >= 0) {
							sb+=(ItemScanner._generatePart(expr, st, nextpos));
						}
					}
				}
				pos = nextpos;
				if (!b) {
					sb+=(ItemScanner._generatePart(expr, oldPos, pos));
				}
			} else {
				++pos;
				sb+=(ItemScanner._generatePart(expr, oldPos, pos));
			}
		}
		if (!bChanged) {
			return null;
		}
		return sb;
	}

	static _getWhiteSpace(expr, lastPos) {
		var st = -1;
		for (var i = lastPos; i >= 0; --i) {
			var aChar = expr.codePointAt(i);
			if (!(ItemScanner._isWhiteSpace(aChar))) {
				break;
			}
			st = i;
		}
		return st;
	}

	static _matchOp(parts, targetObjType, srcParts, srcObjType, newSrcId, bBracket, targetIdforExpr) {
		if ("Calculation" == srcObjType) {
			if (!("Calculation" == targetObjType) && !("Filter" == targetObjType)) {
				return null;
			}
			return ItemScanner._matchGlobalCalcFilter(srcParts, parts, newSrcId, bBracket);
		}
		if ("QuerySubject" == srcObjType) {
			if (!("Calculation" == targetObjType) && !("Filter" == targetObjType) && !("QueryItem" == targetObjType)) {
				return null;
			}
			return ItemScanner._matchQuerySubject(srcParts, parts, newSrcId, bBracket);
		}
		if ("QueryItem" == srcObjType) {
			if (!("Calculation" == targetObjType) && !("Filter" == targetObjType) && !("QueryItem" == targetObjType)) {
				return null;
			}
			return ItemScanner._matchQueryItem(srcParts, parts, newSrcId, bBracket, targetObjType, targetIdforExpr);
		}
		return null;
	}

	static _matchGlobalCalcFilter(srcParts, targetParts, newSrcId, bBracket) {
		if (srcParts.length != 1 || targetParts.length != 1) {
			return null;
		}
		var target = targetParts[0];
		if (bBracket) {
			target = target.substring(1, target.length - 1);
		}
		if (srcParts[0] == target) {
			if (bBracket) {
				return "[" + newSrcId + "]";
			}
			return newSrcId;
		}
		return null;
	}

	static _matchQuerySubject(srcParts, targetParts, newSrcId, bBracket) {
		if (srcParts.length != 1 || targetParts.length==0) {
			return null;
		}
		if (targetParts.length == 1) {
			var target = targetParts[0];
			if (bBracket) {
				target = target.substring(1, target.length - 1);
				var parts = target.split(new RegExp("\\."));
				if (parts.length > 1 && srcParts[0] == parts[0]) {
					parts[0] = newSrcId;
					return "[" + ItemScanner._generateIdFromParts(parts, 0) + "]";
				}
			}
		} else {
			var parts = ItemScanner._generateParts(targetParts);
			if (parts.length > 1) {
				if (bBracket) {
					var p = "[" + srcParts[0] + "]";
					if (p == parts[0]) {
						parts[0] = "[" + newSrcId + "]";
						return ItemScanner._generateIdFromParts(parts, 0);
					}
				} else {
					if (srcParts[0] == parts[0]) {
						parts[0] = newSrcId;
						return ItemScanner._generateIdFromParts(parts, 0);
					}
				}
			}
		}
		return null;
	}

	static _matchQueryItem(srcParts, targetParts, newSrcId, bBracket, targetObjType, targetIdforExpr) {
		if (srcParts.length < 2) {
			return null;
		}
		if ("Calculation" == targetObjType || "Filter" == targetObjType) {
			return ItemScanner._matchQueryItemByIdForExpr(srcParts, targetParts, newSrcId, bBracket);
		}
		if ("QueryItem" == targetObjType) {
			var targetQIParts = targetIdforExpr.split(new RegExp("\\."));
			if (!(srcParts[0] == targetQIParts[0])) {
				return ItemScanner._matchQueryItemByIdForExpr(srcParts, targetParts, newSrcId, bBracket);
			}
			var tParts = ItemScanner._generateParts(targetParts);
			var target = tParts[0];
			if (bBracket) {
				target = target.substring(1, target.length - 1);
			}
			if (!(srcParts[0] == target) && ItemScanner._partsEquals(srcParts, tParts, bBracket, 1)) {
				var parts = newSrcId.split(new RegExp("\\."));
				if (bBracket) {
					for (var i = 0; i < parts.length; ++i) {
						parts[i] = "[" + parts[i] + "]";
					}
				}
				return ItemScanner._generateIdFromParts(parts, 1);
			}
			return ItemScanner._matchQueryItemByIdForExpr(srcParts, targetParts, newSrcId, bBracket);
		}
		return null;
	}

	static _matchQueryItemByIdForExpr(srcParts, targetParts, newSrcId, bBracket) {
		if (targetParts.length == 1) {
			if (bBracket) {
				var target = targetParts[0];
				target = target.substring(1, target.length - 1);
				var parts = target.split(new RegExp("\\."));
				if (ItemScanner._partsEquals(srcParts, parts, false, 0)) {
					return "[" + newSrcId + "]";
				}
			}
		} else {
			var tParts = ItemScanner._generateParts(targetParts);
			if (ItemScanner._partsEquals(srcParts, tParts, bBracket, 0)) {
				if (bBracket) {
					var parts = newSrcId.split(new RegExp("\\."));
					for (var i = 0; i < parts.length; ++i) {
						parts[i] = "[" + parts[i] + "]";
					}
					return ItemScanner._generateIdFromParts(parts, 0);
				}
				return newSrcId;
			}
		}
		return null;
	}

	static _generateIdFromParts(parts, st) {
		var sb = "";
		for (var i = st; i < parts.length; ++i) {
			if (i != st) {
				sb+=(".");
			}
			sb+=(parts[i]);
		}
		return sb;
	}

	static _partsEquals(srcParts, parts, bBracket, st) {
		var srcLength = srcParts.length - st;
		if (srcLength <= 0 || srcLength != parts.length) {
			return false;
		}
		for (var i = st, j = 0; i < srcParts.length; i++, j++) {
			var target = parts[j];
			if (bBracket) {
				target = target.substring(1, target.length - 1);
			}
			if (!(srcParts[i] == target)) {
				return false;
			}
		}
		return true;
	}

	/**
	 * Constructor.
	 */
	//constructor() 
}



// WEBPACK FOOTER //
// ./src/generated/ItemScanner.js